/**
 * UI labels, buttons, headings, and common interface text.
 */
export const ui = {
  actions: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    deleting: "Deleting…",
    deleteForever: "Delete Forever",
    archive: "Archive",
    archiving: "Archiving…",
    unarchive: "Restore",
    unarchiving: "Restoring…",
    edit: "Edit",
    editing: "Editing…",
    create: "Create",
    creating: "Creating…",
    add: "Add",
    remove: "Remove",
    close: "Close",
    confirm: "Confirm",
    retry: "Retry",
    reset: "Reset",
    viewPaper: "View paper",
    viewHighlights: "View highlights",
    highlightPaper: "Highlight paper",
    queuing: "Highlighting…",
    addPaper: "Add paper",
    newAssignment: "New assignment",
    createAssignment: "Create assignment",
    upgrade: "Upgrade to continue",
    signOut: "Sign out",
    redirecting: "Redirecting…",
    signInWithGoogle: "Log in with Google",
    signInWithEmail: "Log in with email",
    createAccount: "Create account",
    creatingAccount: "Creating account…",
    logIn: "Log in",
    loggingIn: "Logging in…",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    resettingPassword: "Resetting…",
    sendResetLink: "Send reset link",
    sendingResetLink: "Sending…",
    resendVerificationEmail: "Resend verification email",
    resendingVerification: "Resending…",
    backToLogin: "Back to login",
    verifyEmail: "Verify email",
    verifyingEmail: "Verifying…",
    manageBilling: "Manage billing",
    showAll: "Show all",
    hideAll: "Hide all",
    selectAll: "Select all",
    showArchived: "Show archived",
    hideArchived: "Hide archived",
    feedback: "Send feedback",
    submit: "Submit",
    submitting: "Submitting…",
    submitWithoutScreenshots: "Submit without screenshots",
    sendReply: "Send reply",
    sendingReply: "Sending…",
    replyViaEmail: "Reply via email",
    generateWithMarkeroo: "Generate with Markeroo",
    stopImpersonation: "Stop impersonation",
    stopping: "Stopping…",
    impersonate: "Impersonate",
    impersonating: "Impersonating…",
    resetToMarkeroo: "Reset to Markeroo version",
    next: "Next",
    continue: "Continue",
    finish: "Finish",
    skip: "Skip",
    fillExampleCriterion: "Fill with example focus area",
    createExampleStudentAndPaper: "Create example student and paper",
    markAsDraft: "Mark as Draft",
    markAsFinal: "Mark as Final",
    copyShareLink: "Copy share link",
    copyEmail: "Copy email",
    sendEmail: "Send email",
    openInEmailClient: "Open in email client",
    resetToDefaults: "Reset to defaults",
    resetting: "Resetting…",
    saveAsNewCriteria: "Save as New Template",
    createTemplate: "Create Template",
    manageCriteriaTemplate: "Manage criteria",
    copyJson: "Copy JSON",
    share: "Share",
    manageShare: "Manage share",
    view: "View",
    viewDetails: "View Details",
    hide: "Hide",
    highlightAgain: "Highlight again",
    createLink: "Create link",
    unshare: "Unshare",
    unsharing: "Unsharing…",
    duplicate: "Duplicate",
    duplicating: "Duplicating…",
    editDetails: "Edit Details",
    startDictation: "Start recording",
    stopRecording: "Stop recording and generate criteria",
    stopStreaming: "Stop and generate criteria",
    addCriteria: "Add Criteria",
    addingCriteria: "Adding Criteria",
    createManually: "Create Manually",
    retryWithVoiceInput: "Try Again with Voice Input",
    retryWithTextInput: "Try Again",
    enable: "Enable",
    disable: "Disable",
    copy: "Copy",
    regenerating: "Regenerating…",
    revoking: "Revoking…",
    generating: "Generating…",
    backToPortal: "All assignments",
    back: "Back",
  },

  loading: {
    default: "Loading…",
    processing: "Highlighting…",
    loadingPapers: "Loading papers…",
    loadingPaper: "Loading paper…",
    loadingEditHistory: "Loading edit history…",
    loadingHighlights: "Loading highlights…",
    generatingCriteria: "Generating criteria…",
    usage: "Loading usage…",
    loadingLogs: "Loading logs…",
    loadingMoreLogs: "Loading more logs…",
    transcribing: "Transcribing audio…",
    connectingToStream: "Connecting…",
    streaming: "Listening…",
    // Playful messages shown during paper highlighting
    processingMessages: [
      "Reading the paper…",
      "Spotting patterns…",
      "Finding the good bits…",
      "Checking the details…",
      "Looking for highlights…",
      "Analyzing carefully…",
      "Making progress…",
      "Reviewing structure…",
      "Examining key points…",
      "Scanning for clarity…",
      "Checking transitions…",
      "Looking at evidence…",
      "Reviewing the intro…",
      "Examining the conclusion…",
      "Spotting opportunities…",
      "Finding strong points…",
      "Checking organization…",
      "Looking at word choice…",
      "Reviewing sentence flow…",
      "Examining paragraphs…",
      "Checking for variety…",
      "Looking at connections…",
      "Reviewing the thesis…",
      "Examining support…",
      "Checking development…",
      "Looking at reasoning…",
      "Reviewing examples…",
      "Spotting details…",
      "Checking coherence…",
      "Finding insights…",
    ],
    /** Format streaming progress text with word count */
    thoughtForWords: (wordCount: string) => `Thought for ${wordCount} words`,
    /** Word count suffix for inline progress display */
    wordsCount: (count: string) => `${count} words`,
    /** Bullet separator for progress display */
    bulletSeparator: "•",
  },

  empty: {
    noPapers: "No papers yet.",
    noClasses: "No classes yet.",
    noClassesTitle: "We’re so glad you’re here!",
    noClassesSubtitle: "Let’s get started by creating your first class 🐣",
    noClassesCreateButton: "Create Your First Class",
    noStudents: "No students yet.",
    noAssignments: "No assignments yet.",
    noCriteria: "No criteria defined.",
    noCriteriaTemplates: "No custom templates yet.",
    noShares: "No shared links.",
    noFeedback: "No feedback submitted.",
    noFeatureFlags: "No feature flags defined.",
    noData: "No data available",
    noHighlightsForPaper: "No highlights available for this paper.",
    noWordsAdded: "No words added yet.",
    noOptionsFound: "No options found",
  },

  headers: {
    appName: "Markeroo",
    assignments: "Assignments",
    papers: "Papers",
    students: "Students",
    classes: "Classes",
    criteria: "Criteria",
    criterion: "Focus Area",
    criteriaTemplates: "Criteria Templates",
    integrations: "Integrations",
    recentAssignments: "Recent assignments",
    archivedAssignments: "Archived assignments",
    archivedStudents: "Archived students",
    archivedClasses: "Archived classes",
    createCriteriaTemplate: "Create New Criteria",
    editCriteriaTemplate: "Edit Criteria",
    editCriteriaTemplateDetails: "Edit Criteria Template",
    shares: "Shared Links",
    settings: "Settings",
    vocabulary: "Vocabulary",
    feedback: "Feedback",
    feedbackDetails: "Feedback Details",
    users: "Users",
    logs: "Logs",
    featureFlags: "Feature Flags",
    createFeatureFlag: "Create Feature Flag",
    account: "Account",
    accountMenu: "Account menu",
    usage: "Usage",
    adminConsole: "Admin console",
    feedbackDashboard: "Feedback dashboard",
    createAssignment: "Create a new assignment",
    saveCriteriaTemplate: "New Criteria Template",
    tutorialError: "Tutorial error",
    documentMarkup: "Feedback",
    paperHighlights: "Paper highlights",
    paperView: "Paper",
    editHistory: "Edit History",
    activeCriteria: "Active Criteria",
    criteriaTemplateForHighlighting: "Criteria for Highlighting Papers",
    starterTemplates: "Starter Templates",
    yourTemplates: "Your Templates",
    editTemplate: "Edit Template",
    emailSettings: "Email Settings",
    emailTemplates: "Email Templates",
    editEmailTemplate: "Edit Email Template",
    newEmailTemplate: "New Email Template",
    legal: "Legal",
    aiDetector: "AI Detector",
  },

  labels: {
    title: "Title",
    name: "Name",
    key: "Key",
    description: "Description",
    descriptionOptional: "Description (optional)",
    instructions: "Instructions",
    paper: "Paper",
    papers: "Papers",
    gradeLevel: "Grade Level",
    gradeRange: "Grade range",
    paperType: "Paper Type",
    paperTypeRequired: "Please select a paper type",
    paperTypeSelectorTitle: "What type of writing is this assignment?",
    paperTypeSelectorSubtitle: "Sets the default criteria for highlighting. You can customize criteria after selecting.",
    paperTypeOpinionArgument: "Opinion / Argument",
    paperTypeOpinionArgumentDescription:
      "Students take a position and support it with reasons and evidence. Grades 3-5 focus on opinions; grades 6+ build arguments with claims, evidence, and counterarguments.",
    paperTypeInformativeExplanatory: "Informative / Explanatory",
    paperTypeInformativeExplanatoryDescription:
      "Students explain a topic clearly using facts, definitions, and concrete details. The goal is to inform, not persuade.",
    paperTypeNarrative: "Narrative",
    paperTypeNarrativeDescription:
      "Students write real or imagined stories with characters, plot, and descriptive details. The goal is to engage the reader through storytelling.",
    paperTypeOther: "Other",
    paperTypeOtherDescription:
      "General writing that doesn’t fit the categories above, such as journals, poetry, or creative responses.",
    status: "Status",
    enabled: "Enabled",
    disabled: "Disabled",
    unset: "Unset",
    set: "Set",
    resetToDefault: "Reset to default?",
    resetToDefaultDescription: (key: string) =>
      `This will reset "${key}" to its default value. The flag will show as "Unset" in the admin console.`,
    resettingEllipsis: "Resetting...",
    draft: "Draft",
    final: "Final",
    readOnly: "Read only",
    student: "Student",
    class: "Class",
    assignment: "Assignment",
    criteria: "Criteria",
    criterion: "Focus Area",
    criteriaTemplate: "Criteria",
    summary: "Summary",
    comment: "Comment",
    says: "says:",
    from: "From:",
    to: "To:",
    untitledCriterion: "Untitled focus area",
    required: "Required",
    optional: "Optional",
    createdAt: "Created",
    submitted: "Submitted",
    updatedAt: "Updated",
    anyType: "Any Type",
    allGrades: "All Grades",
    gradeRangeOptional: "Grade range (optional)",
    elementaryBand: "Elementary (3-5)",
    middleBand: "Middle (6-8)",
    highBand: "High (9-12)",
    custom: "Custom",
    notes: "Notes",
    selectSpecificGrades: "Select specific grades:",
    selectAtLeastOneGrade: "Select at least one grade",
    defaultCriteriaTemplate: "Default criteria",
    starterTemplate: "Starter",
    allGradesFilter: "All grades",
    llmInstructions: "What Markeroo should look for",
    studentFeedbackTemplate: "What to show the student",
    criterionNameHelp: "Short name for this focus area (e.g., 'Thesis', 'Evidence')",
    criterionStudentHelp:
      "When your student looks at these highlights, these are the words they see to tell them what to improve",
    criterionLLMHelp:
      "Markeroo uses these instructions to find the highlights in your students’ papers",
    setAsDefaultCriteriaTemplate: "Set as default criteria for",
    help: "Help",
    upgradeProContinue: "Upgrade to Markeroo Pro to continue highlighting papers.",
    document: "paper",
    documents: "papers",
    allPapersHighlighted: "All papers highlighted",
    limitReached: "Limit reached. Upgrade to continue.",
    of: "of",
    highlightColor: "Highlight color",
    feedbackIntro:
      "Help us improve Markeroo by sharing your thoughts, reporting bugs, or suggesting new features.",
    feedbackQuestion: "What would you like to tell us?",
    screenshotsOptional: "Screenshots (optional)",
    feedbackUploadHint:
      "Upload up to 3 images (PNG, JPEG, GIF, WebP). Max 10MB per file.",
    selectedImages: "Selected images:",
    impersonating: "Impersonating:",
    visibleToStudent: "This will be visible to the student",
    shareCreatesViewOnly: "This will create a view-only link showing:",
    linkActive: "Link active",
    shareLinkActiveFor: "Share link is active for:",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    firstName: "First name",
    lastName: "Last name",
    confirmPassword: "Confirm password",
    currentPassword: "Current password",
    newPassword: "New password",
    welcomeBack: "Welcome back",
    createYourAccount: "Create your account",
    signInToYourAccount: "Sign in to your account",
    signInToContinue: "Sign in to continue",
    orContinueWith: "or",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don’t have an account?",
    checkYourEmail: "Check your email",
    verificationSent: "We’ve sent a verification link to",
    resetPasswordFor: "Reset password for",
    enterNewPassword: "Enter your new password below.",
    forgotPasswordInstructions: "Enter your email and we’ll send you a link to reset your password.",
    passwordResetSent: "Password reset link sent",
    passwordResetInstructions: "Check your email for a link to reset your password. If it doesn’t appear within a few minutes, check your spam folder.",
    emailVerificationRequired: "Please verify your email to continue. Check your inbox for a verification link.",
    passwordRequirements: "Password must be at least 12 characters with uppercase, lowercase, number, and special character.",
    verifyYourEmail: "Verify your email",
    verificationLinkExpired: "This link has expired or is invalid.",
    passwordsDoNotMatch: "Passwords do not match",
    assignmentCreateIntro: "Provide a title to add a fresh assignment to your list.",
    createNewClassOption: "+ Create new class",
    createNewStudentOption: "+ Create new student",
    instructionsGiven: "Instructions given to students",
    paperTypeHelp: "The type of writing determines which criteria are used.",
    failedToLoadAssignments: "Failed to load assignments.",
    createAssignmentFailed: "Failed to create assignment.",
    criteriaTemplateTemplateOptional: "Criteria template (optional)",
    loadingCriteriaTemplates: "Loading criteria…",
    createWithEmptyCriteria: "Create with empty criteria",
    useSmartDefaults: "Use smart defaults based on the class ELA grade level",
    criteriaTemplateName: "Criteria name",
    paperTypeOptional: "Paper type (optional)",
    criteriaTemplatePaperTypeHelp: "Associates these criteria with a specific paper type for easier filtering",
    chooseGradesForCriteriaTemplate: "Choose which grades these criteria are designed for",
    summaryEditorTitle: "Edit paper summary",
    celebrateTitle: "Celebrate",
    celebrateHelp: "What went well in this submission",
    prioritiesTitle: "Priorities",
    prioritiesHelp: "Focus areas to address next",
    strategiesTitle: "Strategies",
    strategiesHelp: "How to take action on the feedback",
    words: "words",
    exceedsLimitParens: "(exceeds limit)",
    wordCount: (count: number, remaining: number) =>
      remaining >= 0
        ? `${count} word${count === 1 ? "" : "s"} (${remaining} remaining)`
        : `${count} words (limit exceeded by ${Math.abs(remaining)})`,
    anonymous: "Anonymous",
    noEmail: "No email",
    page: "Page",
    new: "New",
    inProgress: "In Progress",
    resolved: "Resolved",
    wontFix: "Won’t Fix",
    screenshots: (count: number) => `Screenshots (${count})`,
    loadingScreenshots: (count: number) => `Loading ${count} ${count === 1 ? 'screenshot' : 'screenshots'}…`,
    expandedScreenshot: "Expanded screenshot",
    failedToLoadFeedback: "Failed to load feedback:",
    latest: "Latest",
    all: "All",
    sourceText: "Pasted by teacher",
    sourceStudentWriting: "Written in portal",
    sourceExtension: "Imported from Google Docs",
    feedbackSummary: "Feedback summary",
    noNotes: "No notes yet.",
    criteriaTemplateNotes: "Criteria notes",
    legend: "Legend",
    statusHelp:
      '"Draft" papers identify what needs improvement.\n"Final" papers identify focus areas and suggest example improvements.',
    reduceHighlights: "Reduce Highlights",
    reduceHighlightsHint:
      "Some students may feel overwhelmed by seeing all highlights at once. Check this box to show only key examples for each focus area.",
    teacherCommentsHint:
      "💬 Want to add your own comments? Just select the text you want to highlight with your mouse",
    criteriaGeneratorHint: "Use the generator above to create criteria with Markeroo",
    criteriaHelp: "Criteria help Markeroo highlight each student’s paper",
    voiceRecordingHint: "Speak naturally. Your words appear below in real-time.",
    voiceDictationExplainer: "**Talk** to Markeroo about focus areas for your students and we’ll auto-generate criteria",
    streamingTranscriptLabel: "What we’re hearing:",
    manualInputLabel: "**Write** to Markeroo about focus areas for your students and we’ll auto-generate criteria",
    manualFormLabel: "Manually create a focus area",
    chooseInputMethod: "How would you like to add criteria?",
    speakYourCriteria: "Speak your criteria",
    speakYourCriteriaDescription: "Tell Markeroo what to look for",
    typeOrPaste: "Type or paste",
    typeOrPasteDescription: "Describe your assignment or skills",
    createManuallyDescription: "Fill out the form yourself",
    criterionNameLabel: "Focus Area Name",
    studentDescriptionLabel: "What students see",
    llmDescriptionLabel: "What Markeroo sees",
    or: "OR",
    classNotFound: "Class not found",
    ellLabel: "English Language Learner (ELL)",
    ellHelp: "Simplifies vocabulary and defines terms in feedback",
    useClassDefault: "Use class default",
    addAnotherPaperAfter: "Add another paper after",
    autoQueueHelp: "Highlight the paper and create a private share link",
    failedToLoadUsers: "Failed to load users:",
    noUsersFound: "No users found.",
    joined: "Joined",
    superuser: "Superuser",
    internal: "Internal",
    failedToLoadLogs: "Failed to load logs:",
    noLogsMatchingFilters: "No logs found matching your filters.",
    noMoreLogs: "No more logs to load.",
    logLevel: "Level:",
    serviceLabel: "Service:",
    event: "Event:",
    message: "Message:",
    rawJson: "Raw JSON:",
    noneParens: "(none)",
    noMessageParens: "(no message)",
    retryThisStep: "Retry this step",
    skipThisStepAndContinue: "Skip this step and continue to the next step",
    skipEntireTutorial: "Skip the entire tutorial",
    copied: "Copied!",
    for: "for",
    wordsStudentsShouldKnow: "Words Students Should Know",
    wordsMarkerooShouldAvoid: "Words Markeroo Should Avoid",
    criteriaMatch: "These criteria match your",
    template: "template",
    usingDefaultCriteria: "Using default criteria:",
    defaultCriteriaWork: "These work well for most assignments. If you want something more specific, click \"Add Criteria\" below and describe your assignment—we’ll extract the assessment points automatically.",
    pasteInstructionsOrSkills: "Just paste your assignment instructions, or tell us what skills you want students to focus on. Either works.",
    assignmentStartsWithCriteria: "Your assignment will start with these criteria. You can customize them after you create the assignment.",
    elaGradeLevel: "ELA Grade Level",
    elaGradeLevelWithColon: "ELA Grade Level:",
    elaGrade: (grade: string) => `ELA Grade ${grade}`,
    grade: (grade: string) => `Grade ${grade}`,
    gradeLevelRequired: "Please select a grade level",
    gradeLevelSelectorTitle: "What’s the average ELA grade level for this class?",
    gradeLevelSelectorSubtitle:
      "Sets the default criteria for highlighting. You can customize individual student grade levels and ELL support later.",
    highlightedText: "Highlighted Text",
    explanation: "Explanation",
    suggestionOptional: "Suggestion (Optional)",
    emailTemplateName: "Template Name",
    emailSubject: "Subject",
    emailBody: "Body",
    emailTemplate: "Email Template",
    useDefault: "Use default",
    default: "Default",
    defaultTemplate: "Default",
    templateVariables: "Template Variables",
    templateVariablesHelp: "Use these placeholders in your email template:",
    templateVariablesClickOrDrag: "Click to insert at cursor, or drag into subject or body:",
    insertVariable: "Click to insert",
    noEmailTemplates: "No email templates yet.",
    emailPreview: "Preview",
    emailPreviewEmpty: "Enter text above to see preview",
    studentHasNoEmail: "Student has no email address",
    addStudentEmailFirst: "Add an email address to send emails to this student.",
    emailActionsHelp: "Copy the email to paste into your email client, or click Send to open your default mail app.",
    emailBounced: "Email bounced",
    emailBouncedTooltip: "The last email sent to this student’s address bounced. You may need to update their email address.",
    studentPortal: "Student Portal Link",
    studentPortalHelp: "Students can use this link to view papers you’ve shared with them.",
    regeneratePortalKey: "Regenerate link",
    revokePortalKey: "Revoke access",
    copyPortalLink: "Copy portal link",
    generatePortalLink: "Generate portal link",
    // Writing mode labels
    studentWritingEnabled: "Students can write and submit their papers in Markeroo",
    wordCountRequirements: "Word Count Requirements",
    minWordCount: "Minimum word count",
    maxWordCount: "Maximum word count",
    wordCountOptional: "(optional)",
  },

  errors: {
    loadFeatureFlagsFailed: "Failed to load feature flags",
    loadVocabularyFailed: "Failed to load vocabulary preferences",
    loadHighlightsFailed: "Failed to load highlights",
    loadHighlightsFailedExplanation:
      "There was an error loading the highlights for this paper. Please try again.",
    highlightsNotDisplayed: "Some highlights could not be displayed",
    highlightsErrorExplanation:
      "The document is still viewable, but some highlights may not appear correctly. This usually happens when document structure has changed significantly.",
    technicalDetails: "Technical details",
    // Editor error boundary
    editorErrorTitle: "Something went wrong",
    editorErrorDescription:
      "Don’t worry - your work has been saved. Refreshing the page will restore your paper.",
    editorErrorRecoveryHint:
      "After refreshing, you’ll be able to continue writing where you left off.",
    editorErrorRefreshButton: "Refresh page",
    // Global error boundary
    globalErrorTitle: "Oops! Something went wrong",
    globalErrorDescription:
      "We encountered an unexpected error. Please try reloading the page.",
    globalErrorRefreshButton: "Reload page",
    paperNotFound: "Paper not found",
    paperNotFoundExplanation: "This paper may have been removed or is not shared with you.",
    unableToLoadPaper: "Unable to load paper",
    tryAgainLater: "Please try again later.",
  },

  settings: {
    wordsStudentsKnowDescription:
      "Terms students are expected to know. Markeroo will use these freely in feedback without explanations.",
    wordsMarkerooAvoidDescription:
      "Terms Markeroo should avoid using in feedback to match your students’ vocabulary level.",
    vocabulary: "Vocabulary",
    vocabularyDescription: "Customize which terms appear in student feedback and which terms Markeroo should avoid using.",
    vocabularyPageDescription:
      "Configure vocabulary for Markeroo feedback by ELA grade level. Students receive feedback tailored to their individual ELA grade.",
  },

  emailTemplates: {
    description: "Create and manage email templates for sharing feedback with students.",
  },

  googleClassroom: {
    title: "Google Classroom",
    connected: "Connected",
    notConnected: "Not connected",
    connectedDescription: "Your account is linked to Google Classroom. Classes, students, and assignments sync automatically.",
    notConnectedDescription: "Connect to Google Classroom to automatically import your classes, students, and assignments.",
    enableGCMode: "Connect Google Classroom",
    connecting: "Connecting…",
    disconnectGCMode: "Disconnect",
    disconnecting: "Disconnecting…",
    confirmDisconnectTitle: "Disconnect Google Classroom?",
    confirmDisconnectWarning: "Your Google Classroom data will be kept and become editable in Markeroo.",
    confirmDisconnectDetails: "After disconnecting:",
    confirmDisconnectDetailsList: [
      "All your classes, students, and papers will remain in Markeroo",
      "You’ll be able to edit everything that was synced from Google Classroom",
      "Data will no longer sync with Google Classroom",
      "Reconnecting later will require starting fresh with a new sync",
    ],
    confirmDisconnectButton: "Disconnect",
    // Connect confirmation modal (first-time connection)
    confirmConnectTitle: "Connect to Google Classroom?",
    confirmConnectWarning: "Your existing Markeroo data will be permanently deleted.",
    confirmConnectDetails: "When you connect:",
    confirmConnectDetailsList: [
      "All your existing classes, students, and papers will be deleted",
      "Your data will be replaced with classes imported from Google Classroom",
      "This action cannot be undone",
    ],
    confirmConnectButton: "Connect",
    modeActiveLabel: "Google Classroom Mode",
    manualModeLabel: "Manual Mode",
    connectedSince: "Connected since",
    // Phase 2: Course Import
    importCourses: "Import Classes",
    importFromGoogleClassroom: "Import from Google Classroom",
    importing: "Importing…",
    selectCourses: "Select classes to import",
    selectCoursesDescription: "Choose which Google Classroom courses to import as Markeroo classes.",
    noCourses: "No courses available",
    noCoursesDescription: "You don’t have any active courses in Google Classroom, or all courses have already been imported.",
    alreadyImported: "Already imported",
    courseSection: "Section",
    studentsWillSync: "Students will be synced automatically",
    importSelected: "Import Selected",
    importingCourses: "Importing classes…",
    lastSynced: "Last synced",
    // Phase 3: Sync Button
    syncFromGoogleClassroom: "Sync from Google Classroom",
    syncing: "Syncing…",
    gcModeDisabledTooltip: "In GC Mode, make this change in Google Classroom",
    addStudentInGC: "Add students in Google Classroom",
    inviteStudents: "Invite Students",
    inviteStudentsInGC: "Invite students in Google Classroom",
    createAssignmentInGC: "Create assignments in Google Classroom",
    titleSyncedFromGC: "This title is synced from Google Classroom",
    managedByGoogleClassroom: "Managed by Google Classroom",
    syncedAgo: (time: string) => `Synced ${time}`,
    never: "never",
    // Token expiration banner
    connectionExpiredTitle: "Google Classroom connection expired",
    connectionExpiredDescription: "Your Google Classroom authorization has expired. Reconnect to continue syncing classes, students, and assignments.",
    reconnect: "Reconnect",
    // Write scopes banner (for student writing feature)
    writeScopesRequiredTitle: "Additional permissions required",
    writeScopesRequiredDescription: "To enable student writing for Google Classroom assignments, please reconnect to grant additional permissions.",
    grantPermissions: "Grant permissions",
    // Redirect modal (for actions that must be done in GC)
    redirectModalTitle: "Go to Google Classroom",
    redirectModalDescription: "This action needs to be done in Google Classroom. Your changes will sync back to Markeroo automatically.",
    goToGoogleClassroom: "Go to Google Classroom",
    dontShowAgain: "Don’t show this again",
    // Reconnect confirmation modal
    confirmReconnectTitle: "Reconnect to Google Classroom?",
    confirmReconnectWarning: "Your previously imported data will be replaced with fresh data from Google Classroom.",
    confirmReconnectDetails: "When you reconnect:",
    confirmReconnectDetailsList: [
      "All classes, students, and papers you edited after disconnecting will be deleted",
      "Fresh data will be synced from Google Classroom",
      "Any changes you made will be lost",
    ],
    confirmReconnectSuggestion: "If you want to keep your edits, consider exporting or backing up first.",
    confirmReconnectButton: "Reconnect",
  },

  placeholders: {
    enterWord: "Enter a word...",
  },

  tooltips: {
    addOwnComment: "Add your own comment",
    addCommentHelp: "Click 'Add comment' to add your own custom highlight to this phrase!",
  },

  highlightNavigation: {
    title: "Highlights",
    activeSection: "Active",
    completedSection: "Completed",
    completedLabel: "completed",
    showResolved: "Show resolved highlights",
    teacherCommentLabel: "Teacher Comment",
    emptyState: "No highlights to display",
    emptyStateHint: "Highlights will appear here after assessment",
    markComplete: "Mark as complete",
    markIncomplete: "Mark as incomplete",
    completedIcon: "Resolved",
    resolvedTooltip:
      "This issue has been marked as resolved by the student in their next version",
    editHighlight: "Edit highlight",
    deleteHighlight: "Delete highlight",
    nextHighlight: "Next highlight",
    previousHighlight: "Previous highlight",
    deselectHighlight: "Deselect highlight",
    allResolvedTitle: "Nice work! You’ve resolved all feedback for this paper",
    allResolvedMessage:
      "You can review your changes or close this view when you’re ready.",
    highlightCount: (count: number) => `${count} ${count === 1 ? 'highlight' : 'highlights'}`,
    highlightPosition: (current: number, total: number) => `${current} of ${total}`,
    // Progress bar
    progressLabel: "Progress",
    progressCount: (resolved: number, total: number) =>
      `${resolved} of ${total} resolved`,
    // Resolve/Unresolve buttons
    resolve: "Resolve",
    unresolve: "Unresolve",
    unresolveDisabledTooltip:
      "Document has changed since this highlight was resolved",
  },

  hotkeys: {
    nextHighlight: "Next highlight",
    previousHighlight: "Previous highlight",
    markComplete: "Mark complete",
    editHighlight: "Edit highlight",
    deleteHighlight: "Delete highlight",
    deselectHighlight: "Deselect",
  },

  billing: {
    successTitle: "Welcome to Markeroo Pro!",
    successMessage:
      "Your subscription is now active. Enjoy unlimited paper highlights!",
    continueToDashboard: "Continue to dashboard",
    canceledTitle: "Checkout canceled",
    canceledMessage:
      "Your checkout was canceled. No charges were made to your account.",
    returnToDashboard: "Return to dashboard",
    questionsContact: "Questions? Contact us at support@markeroo.com",
    subscriptionEndsOn: (date: string) => `Your subscription ends on ${date}`,
    resubscribe: "Resubscribe",
  },

  pricing: {
    title: "Upgrade to Markeroo Pro",
    subtitle: "Choose the plan that works best for you",
    monthlyPlan: "Monthly",
    annualPlan: "Annual",
    monthlyPrice: "$15",
    annualPrice: "$120",
    perMonth: "/month",
    perYear: "/year",
    annualEquivalent: "$10/month",
    annualSavings: "Save 33%",
    mostPopular: "Most Popular",
    selectMonthly: "Select Monthly",
    selectAnnual: "Select Annual",
    features: {
      title: "Everything in Pro",
      papersPerMonth: "Unlimited papers",
      unlimitedClasses: "Unlimited classes & students",
      allPaperTypes: "All paper types",
      customCriteria: "Custom criteria templates",
      prioritySupport: "Priority support",
    },
    freePlanNote: "Free plan: 5 papers per month",
    alreadySubscribed: "You’re already subscribed to Markeroo Pro",
    manageSubscription: "Manage your subscription",
    // Upgrade flow for monthly subscribers
    currentPlanMonthly: "You’re currently on the Monthly plan",
    upgradeToAnnual: "Upgrade to Annual",
    upgradeToAnnualDescription: "Save 33% by switching to annual billing",
    upgradeConfirmTitle: "Upgrade to Annual Billing?",
    upgradeConfirmMessage: "You’ll be charged a prorated amount for the remainder of your billing period. Your new annual subscription will begin immediately.",
    upgradeConfirm: "Yes, upgrade to annual",
    upgradeCancel: "Cancel",
    upgradeSuccess: "Successfully upgraded to annual billing!",
    upgradeError: "Failed to upgrade subscription. Please try again.",
  },

  trial: {
    title: "30-Day Free Trial",
    subtitle: "Try all Pro features free for 30 days",
    startTrial: "Start Free Trial",
    daysRemaining: (days: number) =>
      days === 1 ? "1 day left in trial" : `${days} days left in trial`,
    trialEndsOn: (date: string) => `Trial ends on ${date}`,
    trialExpired: "Your trial has ended",
    upgradeNow: "Upgrade Now",
    features: {
      highlight: "Highlight up to 50 papers per month",
      allFeatures: "Access all Pro features",
      noCreditCard: "No credit card required",
    },
  },

  legal: {
    description: "Review our legal documents and policies.",
    privacyPolicy: "Privacy Policy",
    privacyPolicyDescription: "Learn how we collect, use, and protect your data and your students’ data.",
    termsOfService: "Terms of Service",
    termsOfServiceDescription: "Review the terms and conditions for using Markeroo.",
    security: "Security Practices",
    securityDescription: "Learn about the security measures we use to protect your data.",
    dataProcessingAddendum: "Data Processing Addendum",
    dataProcessingAddendumDescription: "Review our data processing terms for schools and educators.",
  },

  compliance: {
    dpaAcceptLabel: "I agree to the",
    dpaLinkText: "Data Processing Addendum",
    signUpAgreement: "By signing up, you agree to our",
    noticeToParents: "Notice to Parents",
    noticeToParentsDescription: "A template notice for parents about our data practices.",
  },

  contact: {
    supportEmail: "support@markeroo.com",
    supportEmailHref: "mailto:support@markeroo.com",
  },

  account: {
    title: "Account",
    description: (email: string) => `Manage your account settings and subscription for ${email}.`,
    preferredNameLabel: "Preferred Name",
    preferredNameDescription: "How your students should address you in emails (e.g., 'Mr. Smith', 'Ms. Johnson'). If empty, your full name will be used.",
    preferredNamePlaceholder: "e.g., Mr. Smith",
    studentWritingLabel: "Student Writing",
    studentWritingDescription: "Allow students to write and submit their papers in Markeroo. When enabled, students can use the writing interface to compose and submit drafts.",
    studentWritingEnabledLabel: "Enable student writing",
    studentWritingDisabledLabel: "Disable student writing",
    studentWritingEnabledDescription: "Students can write and submit papers in Markeroo",
    studentWritingDisabledDescription: "Students cannot write or submit papers in Markeroo",
    emailLabel: "Email Address",
    subscriptionLabel: "Subscription",
    subscriptionPlan: "Plan",
    subscriptionStatus: "Status",
    subscriptionActive: "Active",
    subscriptionCanceled: "Canceled",
    subscriptionNone: "Free",
    cancelSubscription: "Cancel Subscription",
    manageSubscription: "Manage Subscription",
    subscriptionEndsOn: (date: string) => `Ends on ${date}`,
    billingMonthly: "Monthly",
    billingAnnual: "Annual",
    // Account deletion
    deleteAccountLink: "Delete my account",
    deleteAccountTitle: "Delete Account?",
    deleteAccountWarning: "This action is permanent and cannot be undone.",
    deleteAccountDescription: "When you delete your account, the following will be permanently removed:",
    deleteAccountList: [
      "All your classes and students",
      "All assignments and papers",
      "All criteria templates",
      "Your subscription (if active)",
    ],
    deleteAccountConfirmPrompt: (email: string) =>
      `To confirm, type "delete ${email}" below:`,
    deleteAccountConfirmPlaceholder: "Type here to confirm...",
    deleteAccountButton: "Delete Account",
    deleteAccountDeleting: "Deleting...",
    deleteAccountImpersonationError: "Cannot delete account while impersonating",
    // Account deleted page
    accountDeletedTitle: "Account Deleted",
    accountDeletedMessage: "Your account and all associated data have been permanently deleted.",
    accountDeletedCta: "Return to Home",
  },

  analysisMode: {
    title: "Highlighting Mode",
    description: "Choose how Markeroo highlights your papers.",
    rapidReview: "Rapid Review",
    rapidReviewTime: "~10 sec per paper",
    rapidReviewDescription: "Best for daily assignments, quick checks, and spotting high-level rubric criteria. Keeps your grading flow moving.",
    deepAnalysis: "Deep Analysis",
    deepAnalysisTime: "~2 min per paper",
    deepAnalysisDescription: "Best for major essays and final papers. Takes a moment to analyze logic, structure, and complex criteria deeply.",
    active: "Active",
  },

  formatting: {
    gradeSingle: (grade: number) => `Grade ${grade}`,
    gradeRange: (start: number, end: number) => `Grades ${start}-${end}`,
    gradeList: (grades: string) => `Grades ${grades}`,
  },

  forceUpdate: {
    title: "New Features Incoming!",
    message:
      "You’re about to get some new features! The page will refresh automatically.",
    countdownPrefix: "Refreshing in",
    countdownSuffix: "seconds...",
    refreshNow: "Refresh Now",
    // Admin page strings
    adminTitle: "Force Update",
    adminDescription:
      "Force all connected clients to refresh and load the latest version of the application.",
    triggerButton: "Trigger Force Update",
    triggering: "Triggering...",
    triggerSuccess: "Force update triggered successfully",
    triggerFailed: "Failed to trigger force update",
    historyTitle: "Update History",
    noHistory: "No force updates have been triggered yet.",
    setAt: "Set at",
    setBy: "by",
    reason: "Reason",
    noReason: "No reason provided",
    rollback: "Roll Back",
    rollingBack: "Rolling back...",
    rollbackSuccess: "Rolled back successfully",
    rollbackFailed: "Failed to roll back",
    currentTimestamp: "Current Timestamp",
    notSet: "Not set",
    reasonLabel: "Reason (optional)",
    reasonPlaceholder: "e.g., Deployed new feature, Fixed critical bug",
  },

  notFound: {
    title: "Page Not Found",
    message: "Oops! Looks like this page hopped away.",
    subMessage: "The page you’re looking for doesn’t exist or has been moved.",
    backToHome: "Back to Home",
  },

  portal: {
    writingNotAvailable: "Writing not available",
    writingNotAvailableDescription: "Writing is not currently available for this assignment.",
    backToPortal: "Back to portal",
    saveAndExit: "Save & exit",
    submitForFeedback: "Submit for feedback",
    // Autosave status
    saving: "Saving...",
    saved: "Saved",
    savedAt: (time: string) => `Saved at ${time}`,
    saveFailed: "Save failed",
    retrying: "Retrying...",
    offline: "Offline",
    localBackupFailed: "Local backup unavailable",
    // Draft recovery modal
    draftRecoveryTitle: "Recover unsaved work?",
    draftRecoveryDescription: "We found a more recent version of your work saved locally. This may happen if your browser closed unexpectedly or you lost internet connection.",
    draftRecoveryTimestamp: (time: string) => `Local backup from ${time}`,
    draftRecoveryRecover: "Recover local version",
    draftRecoveryDiscard: "Use server version",
    // Version creation
    startNewDraft: "Start new draft",
    startingNewDraft: "Starting…",
    newDraftCreated: "New draft created!",
  },

  editMetrics: {
    title: "Writing Analysis",
    riskLow: "Low risk",
    riskMedium: "Medium risk",
    riskHigh: "High risk",
    noMetrics: "No writing data",
    noMetricsDescription: "No edit history available for this paper.",
    loadError: "Unable to load writing analysis",
    // Metric labels
    wordsPerMinute: "Words per minute",
    pastePercentage: "Paste percentage",
    sessionDuration: "Session duration",
    totalKeystrokes: "Total keystrokes",
    errorCorrectionRate: "Error correction rate",
    pasteCount: "Paste operations",
    largestPaste: "Largest paste",
    burstCount: "Typing bursts",
    activeWritingTime: "Active writing time",
    // Additional metric labels for modal
    overallAssessment: "Overall Assessment",
    wpm: "WPM",
    avgKeystrokeInterval: "Avg keystroke interval",
    pasteRatio: "Paste ratio",
    totalDuration: "Total duration",
    sessions: "Sessions",
    errorCorrection: "Error correction",
    totalEdits: "Total edits",
    undoRedo: "Undo/Redo",
    totalEventsAndWords: (events: string, words: string) =>
      `${events} total events \u2022 ${words}`,
    // Risk explanations
    riskExplanation: "Risk is calculated based on typing patterns, paste behavior, and editing patterns.",
    highPasteWarning: "High paste percentage may indicate copied content.",
    lowErrorCorrectionWarning: "Very low error correction may indicate pre-written content.",
    highWpmWarning: "Unusually high WPM may indicate pasted content.",
    largePasteWarning: "A large block of text was pasted in a single operation.",
    warnings: "Warnings",
    // Actions
    flagForReview: "Flag for review",
    flagged: "Flagged",
    viewDetails: "View writing analysis",
    // Tooltips
    wpmTooltip: "Based on active typing time, excluding pauses over 30 seconds.",
    pasteTooltip: "Percentage of final text that was pasted vs. typed.",
    errorCorrectionTooltip: "Ratio of deletions to insertions. Natural typing is typically 0.2-0.4.",
    // Units
    minutes: "min",
    seconds: "sec",
    characters: "chars",
    words: "words",
    ms: "ms",
    // View toggle
    summary: "Summary",
    details: "Details",
    // Edit replay
    editReplay: "Edit Replay",
    viewEditReplay: "View Edit Replay",
    editEvents: "edit events",
    loadEventsFailed: "Unable to load edit events for replay.",
  },

  mfa: {
    // Headers
    title: "Multi-Factor Authentication",
    setupTitle: "Set Up MFA",
    verifyTitle: "Verify MFA",
    securityTitle: "Admin Security",
    // Setup flow
    setupDescription:
      "Multi-factor authentication adds an extra layer of security to the admin console. Once enabled, you’ll need to enter a code from your authenticator app to access admin features.",
    step1ScanQr: "Step 1: Scan QR Code",
    step1Description: "Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)",
    step2EnterCode: "Step 2: Enter Verification Code",
    step2Description: "Enter the 6-digit code from your authenticator app to complete setup",
    manualEntryLabel: "Or enter this code manually:",
    // Recovery codes
    recoveryCodes: "Recovery Codes",
    recoveryCodesDescription:
      "Save these recovery codes in a secure location. Each code can only be used once. If you lose access to your authenticator app, you can use these codes to regain access.",
    recoveryCodesWarning:
      "These codes will only be shown once. Store them securely now.",
    recoveryCodesCopied: "Recovery codes copied to clipboard",
    copyRecoveryCodes: "Copy Recovery Codes",
    regenerateRecoveryCodes: "Generate New Recovery Codes",
    // Verification
    enterCode: "Enter your 6-digit code",
    enterCodeOrRecovery: "Enter your 6-digit code or recovery code",
    verifyButton: "Verify",
    verifying: "Verifying...",
    // Status
    enabled: "MFA Enabled",
    notEnabled: "MFA Not Enabled",
    enabledAt: "Enabled",
    lastUsed: "Last used",
    // Actions
    enableMfa: "Enable MFA",
    disableMfa: "Disable MFA",
    setupMfa: "Set Up MFA",
    // Admin enforcement
    mfaRequired: "MFA Required",
    mfaRequiredDescription:
      "Admin console access requires multi-factor authentication. Please verify your identity to continue.",
    sessionExpired: "Your MFA session has expired. Please verify again.",
    // Feature flag
    adminMfaEnforcement: "Admin MFA Enforcement",
    adminMfaEnforcementDescription:
      "When enabled, all admin console access requires MFA verification.",
    enableFeatureFlagHint:
      "Enable the admin_mfa_enforcement feature flag to require MFA for admin console access.",
  },
  // Keyboard shortcuts help modal
  keyboardShortcuts: {
    title: "Keyboard Shortcuts",
    formatting: "Formatting",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    heading1: "Heading 1",
    heading2: "Heading 2",
    heading3: "Heading 3",
    paragraph: "Normal text",
    blockquote: "Blockquote",
    codeBlock: "Code block",
    clipboard: "Clipboard",
    pasteAsPlainText: "Paste as plain text",
    pasteWithFormatting: "Paste with formatting",
    actions: "Actions",
    save: "Save",
    submit: "Submit",
    undo: "Undo",
    redo: "Redo",
    find: "Find in document",
    findReplace: "Find and replace",
    navigation: "Navigation",
    exitEditor: "Exit editor",
    showHelp: "Show this help",
    browserOverrideNote:
      "Note: Some shortcuts override browser defaults (e.g., save shortcut saves your document instead of the page).",
  },

  aiDetector: {
    // Headers
    title: "AI Detector",
    documents: "Documents",
    documentDetail: "Document Details",
    // Pre-launch banner
    extensionComingSoonTitle: "Chrome Extension Launching Soon",
    extensionComingSoonDescription:
      "Our Chrome extension for seamless Google Docs integration is currently being prepared for publication. You’ll be able to analyze writing patterns directly from Google Docs once it’s available.",
    // Feature discovery banner
    featureDiscoveryTitle: "AI Writing Detector",
    featureDiscoveryDescription:
      "Detect AI-generated content in Google Docs with our Chrome extension. Analyze typing patterns, paste detection, and writing behavior.",
    featureDiscoveryLearnMore: "Learn More",
    featureDiscoveryViewDocuments: "View Documents",
    // Empty state
    emptyTitle: "No documents yet",
    emptyDescription:
      "Install the Chrome extension to start detecting AI-generated content in Google Docs.",
    emptyInstallButton: "Install Extension",
    emptyLearnMore: "Learn More",
    // Document list
    importedAt: "Imported",
    wordCount: "Words",
    riskLevel: "Risk Level",
    viewDocument: "View",
    deleteDocument: "Delete",
    deleteAllDocuments: "Delete All Documents",
    exportAllData: "Export Data",
    // Risk levels
    riskNone: "None",
    riskLow: "Low",
    riskMedium: "Medium",
    riskHigh: "High",
    // Document detail
    openInGoogleDocs: "Open in Google Docs",
    backToDocuments: "Back to Documents",
    riskLevelDescription: "Based on writing patterns and edit behavior",
    documentInfo: "Document Information",
    author: "Author",
    analyzed: "Analyzed",
    lastAnalyzed: "Last Analyzed",
    viewDetails: "View Details",
    viewSummary: "View Summary",
    summary: "Summary",
    details: "Details",
    detailedAnalysis: "Detailed writing behavior analysis",
    writingAnalysis: "Writing Analysis",
    writingReplay: "Writing Replay",
    writingReplayDescription: "Watch how the document was written over time",
    watchReplay: "Watch Replay",
    replayComingSoon: "Replay functionality coming soon...",
    noEventsAvailable: "No edit events available for this document",
    replayPosition: "Replay timeline position",
    event: "Event",
    beforeFirstEvent: "Before first event",
    words: "words",
    characters: "characters",
    // Loading
    loadingDocuments: "Loading documents…",
    loadingDocument: "Loading document…",
    // Errors
    loadDocumentsFailed: "Failed to load documents",
    loadDocumentFailed: "Failed to load document",
    deleteDocumentFailed: "Failed to delete document",
    deleteAllDocumentsFailed: "Failed to delete documents",
    exportDataFailed: "Failed to export data. Please try again.",
    // Confirmations
    deleteDocumentConfirmTitle: "Delete Document",
    deleteDocumentConfirmMessage:
      "Are you sure you want to delete this document? This action cannot be undone.",
    deleteAllDocumentsConfirmTitle: "Delete All Documents?",
    deleteAllDocumentsConfirmMessage: (count: number) =>
      `Are you sure you want to permanently delete all ${count} analyzed ${count === 1 ? "document" : "documents"}? This action cannot be undone. You can re-import documents from Google Docs later.`,
    exportDataTitle: "Export Your Data",
    exportDataDescription:
      "Download all your AI detection data in a machine-readable format. This export is provided for GDPR compliance and data portability.",
    exportDataIncludesTitle: "Your export will include:",
    exportDataIncludesDocuments: (count: number) =>
      `${count} analyzed ${count === 1 ? "document" : "documents"} with metadata`,
    exportDataIncludesMetrics: "AI detection analysis results and risk assessments",
    exportDataIncludesEditHistory: "Complete edit history for all documents",
    exportDataIncludesTimestamps: "Import dates and analysis timestamps",
    exportDataFormatNote:
      "Data will be exported in NDJSON format (newline-delimited JSON), suitable for import into other systems or archival purposes.",
    downloadExport: "Download Export",
    exportingData: "Exporting…",
    // Success
    documentDeleted: "Document deleted",
    allDocumentsDeleted: (count: number) =>
      `${count} ${count === 1 ? "document" : "documents"} deleted`,
    exportDataSuccess: "Export downloaded successfully",
    // License / quota
    quotaUsed: (used: number, limit: number) =>
      `${used} of ${limit} documents used`,
    quotaUnlimited: "Unlimited documents",
    quotaExceeded: "Document limit reached",
    quotaExceededDescription:
      "You’ve reached your free tier limit. Upgrade to Pro for unlimited AI detection.",
    upgradeForMore: "Upgrade for More",
    // Replay controls
    play: "Play",
    pause: "Pause",
    skipToStart: "Skip to start",
    skipToEnd: "Skip to end",
    stepBack: "Previous event",
    stepForward: "Next event",
    speed: "Speed",
    playbackSpeed: "Playback speed",
    pasteEvent: "Paste",
    insertEvent: "Insert",
    deleteEvent: "Delete",
    replaceEvent: "Replace",
    undoEvent: "Undo",
    redoEvent: "Redo",
    // Timeline visualization
    activityTimeline: "Edit activity over time",
    sessionBreak: "Session break",
    pasteMarker: "Paste event marker",
    // Playback controls section
    playbackControls: "Playback Controls",
    gapSkipping: "Gap Skipping",
    // Auto-skip feature
    autoSkip: "Auto-skip",
    autoSkipOn: "Auto-skip enabled - fast review mode with compressed time gaps",
    autoSkipOff: "Auto-skip disabled - real-time playback respecting actual writing timing",
    skippingGap: (duration: string) => `Skipping ${duration} of inactivity`,
    // Stats panel
    edits: "Edits",
    pastes: "Pastes",
    time: "Time",
    wordsLabel: "Words",
    // Paste frequency track
    noPasteEvents: "No paste events",
    pasteCharsSuffix: "chars pasted",
    // Speed control popup
    speedControlTitle: "Playback Speed",
    speedControlLabel: (speed: number, epm: number) =>
      `Playback speed: ${speed}x (${epm} edits per minute)`,
    speedSliderLabel: "Adjust playback speed",
    epmSuffix: "EPM",
    // Insufficient data warning (few edits on substantial document)
    insufficientData: "Few Edits Detected",
    insufficientDataWarning:
      "This document has very few recorded edits. Documents written directly in the editor typically show hundreds of edits from typing, corrections, and revisions. This may indicate content was composed elsewhere, or may have other explanations such as dictation or file conversion.",
    insufficientDataShort: "Few edits recorded",
    // Multi-author warning
    multiAuthor: "Multiple Authors Detected",
    multiAuthorWarning:
      "This document appears to have been edited by multiple people. The risk assessment may not accurately reflect any single author’s writing pattern. Consider this when interpreting the results.",
    multiAuthorShort: "Multiple authors detected",
    multiAuthorCount: (count: number) =>
      `${count} distinct editing session${count !== 1 ? "s" : ""} detected`,
    // Editors panel
    editors: "Editors",
    editsLabel: "edits",
    // Search in edit history
    searchHistory: "Search Edit History",
    searchPlaceholder: "Search text added or removed...",
    searchResults: "Search results",
    caseSensitive: "Case sensitive",
    caseSensitiveToggle: "Aa",
    noResultsFound: "No matches found",
    resultsCount: (count: number) =>
      `${count} ${count === 1 ? "result" : "results"}`,
    resultsLimited: (count: number) =>
      `Showing first ${count} results`,
    foundInAdded: "Added text",
    foundInDeleted: "Deleted text",
    // Feature disabled state
    featureDisabledTitle: "Coming Soon",
    featureDisabledMessage:
      "The AI Detector feature is not yet available. Check back soon!",
    featureDisabledBackButton: "Back to Classes",
    // Breadcrumb navigation
    keyMoments: "Key Moments",
    backToKeyMoments: "Back to key moments",
    moment: (index: number, summary: string) => `Moment ${index}: ${summary}`,
    // Replay document
    noContentYet: "No content yet",

    // === VERDICT BADGES ===
    verdictSuspicious: "Suspicious Activity",
    verdictUnusual: "Unusual Patterns",
    verdictNormal: "Normal Writing",
    verdictNoData: "No Edit History",

    verdictSuspiciousDetail: "This paper shows unusual writing patterns",
    verdictNormalDetail: "This paper shows typical student writing",
    verdictDisclaimer:
      "This analysis reflects editing patterns only and is not a determination of authorship or academic integrity. It should inform—not replace—educator judgment.",

    // === MOMENT TYPES ===
    momentTypePaste: "Pasted",
    momentTypeTyped: "Typed",
    momentTypeDeleted: "Deleted",
    momentTypeSessionStart: "Session resumed",
    momentTypeUndo: "Undid",

    // === MOMENT SUMMARIES ===
    pastedWords: (count: number) =>
      `Pasted ${count} ${count === 1 ? "word" : "words"}`,
    typedWords: (count: number) =>
      `Typed ${count} ${count === 1 ? "word" : "words"}`,
    typedCharacters: (count: number) =>
      `Typed ${count} ${count === 1 ? "character" : "characters"}`,
    deletedWords: (count: number) =>
      `Deleted ${count} ${count === 1 ? "word" : "words"}`,
    fixedTypo: "Fixed typo",
    sessionResumed: "Session resumed",

    // === DURATION ===
    durationInstant: "Instant",
    durationSeconds: (count: number) => `${count} sec`,
    durationMinutes: (count: number) => `${count} min`,

    // === NAVIGATION ===
    previousMoment: "Previous",
    nextMoment: "Next",

    // === PROGRESS ===
    momentProgress: (current: number, total: number) =>
      `Moment ${current} of ${total}`,

    // === DRILL DOWN ===
    seeEdits: "See edits",
    editsInMoment: (count: number) =>
      `${count} ${count === 1 ? "edit" : "edits"}`,
    thisContainsEdits: (count: number) =>
      `This moment contains ${count} ${count === 1 ? "edit" : "edits"}`,

    // === VERDICT FINDINGS ===
    findingPastePercent: (percent: number) =>
      `${percent}% of this paper was pasted`,
    findingPastePercentDetailHigh: "Most content appeared instantly, not typed",
    findingPastePercentDetailMedium: "Content appeared faster than typical typing",
    findingLargePaste: (words: number) => `${words} words pasted at once`,
    findingLargePasteDetail: "The largest paste was most of the paper",
    findingLowTypingTime: (minutes: number) =>
      `Only ${minutes} minutes of actual typing`,
    findingLowTypingTimeDetail: "Very little time spent writing",
    findingMultipleSessions: (days: number) => `Written over ${days} days`,
    findingMultipleSessionsDetail: "Multiple writing sessions spread out",
    findingNormalTypingTime: (minutes: number) =>
      `${minutes} minutes of active typing`,
    findingNormalTypingTimeDetail: (wordCount: string) =>
      `Typical for a ${wordCount}-word essay`,
    findingTypoCorrections: "Fixed many typos while writing",
    findingTypoCorrectionsDetail: "Typical of natural writing",
    findingNoTypoCorrections: "Almost no typos fixed",
    findingNoTypoCorrectionsDetail: "Unusual for handwritten content",
  },

  // Accessibility announcements for screen readers
  accessibility: {
    formatApplied: (formatName: string) => `${formatName} applied`,
    formatRemoved: (formatName: string) => `${formatName} removed`,
  },

  // Find and Replace dialog
  findReplace: {
    title: "Find",
    searchPlaceholder: "Search...",
    replacePlaceholder: "Replace with...",
    noMatches: "No results",
    previous: "Previous",
    next: "Next",
    caseSensitive: "Match case",
    replace: "Replace",
    replaceAll: "Replace all",
    showReplace: "Show replace",
    hideReplace: "Hide replace",
    hintEnterKey: "Enter",
    hintShiftEnterKey: "Shift+Enter",
    hintEscKey: "Esc",
    hintNext: "next",
    hintPrev: "prev",
    hintClose: "close",
  },

  // Document outline panel
  outline: {
    title: "Outline",
    emptyState: "Add headings to see your outline",
    emptyStateHint: "Use the H1, H2, or H3 buttons to create headings",
    sectionCount: (count: number) =>
      `${count} ${count === 1 ? "section" : "sections"}`,
    showOutline: "Show outline",
    hideOutline: "Hide outline",
    jumpToSection: "Jump to section",
  },

  // Criteria categories for teacher comments
  categories: {
    label: "Category",
    claims: "Claims",
    claimsDescription: "Thesis statements, opinions, and positions",
    support: "Support",
    supportDescription: "Evidence, facts, and supporting details",
    reasoning: "Reasoning",
    reasoningDescription: "Analysis and logical explanations",
    flow: "Flow",
    flowDescription: "Organization and transitions",
    style: "Style",
    styleDescription: "Voice, tone, and word choice",
    mechanics: "Mechanics",
    mechanicsDescription: "Grammar, spelling, and punctuation",
    other: "Other",
    otherDescription: "Miscellaneous feedback",
  },
} as const;

export type UIString = typeof ui;
